import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Title, downloadCSV, usePermissions, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DownloadIcon from '@mui/icons-material/Download';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTipOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweepOutlined';
import BoltIcon from '@mui/icons-material/BoltOutlined';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import TimelineIcon from '@mui/icons-material/Timeline';
import { UsagePurgeDialog } from './UsagePurgeDialog';
import { ActionsView, ClicksView, TimelineView } from './UsageTraceViews';
import { EXPORT_ALL_BUTTON, EXPORT_ALL_ERROR, TRACE_NOTE, downloadUsageExport } from './usageTrace';
import { Tile, TileGrid } from '../dashboard/Tile';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { toCsv, type CsvColumn } from '../../shared/csvExport';
import { FEATURE_IMPERSONATION } from '../users/impersonationRules';
import { clientDayKey } from '../../shared/dateTime';
import {
  ADMIN_ONLY_NOTE,
  CONSENT_NOTE,
  DAILY_CSV,
  ELEMENT_CSV,
  ERROR_CSV,
  FEATURE_OFF_NOTE,
  FEATURE_USAGE_TELEMETRY,
  INTRO_NOTE,
  IMPERSONATION_NOTE,
  IMPERSONATION_OPTIONS,
  LOAD_ERROR,
  PATHS_NOTE,
  PURGE_BUTTON,
  purgeDoneText,
  retentionText,
  PATH_CSV,
  SCREEN_CSV,
  USAGE_REPORT_ROLE,
  departmentLabel,
  durationText,
  environmentLabel,
  fetchUsageReport,
  roleLabel,
  routeLabel,
  stopsText,
  unmeasuredText,
  userLabel,
  type UsageQuery,
  type UsageReport,
} from './usageApi';

/**
 * „Pomiar użycia” (decyzja użytkownika 22.10.2026, S-22.10-02; przełącznik
 * `usageTelemetry`) — sekcja administracyjna, tylko rola ADMIN (rozstrzyga
 * API: 403 każdemu innemu, 404 przy wyłączonym przełączniku).
 *
 * Filtry: okres (dni polskie), dział, rola, konto, środowisko. Widoki:
 * ścieżki z paczki (zaczęte i ukończone przejścia, zatrzymania, mediana
 * czasu), najczęstsze ekrany i przyciski, błędy API według ekranu,
 * aktywność dzienna według działu. Każdy widok ma eksport CSV. To odczyt
 * zachowania, nie ocena osób — bez rankingu kont.
 */

type TabKey = 'paths' | 'screens' | 'actions' | 'clicks' | 'timeline' | 'errors' | 'daily';

const TABS: { key: TabKey; label: string; icon: ReactElement }[] = [
  { key: 'paths', label: 'Ścieżki pracy', icon: <AltRouteIcon fontSize="small" /> },
  { key: 'screens', label: 'Ekrany i przyciski', icon: <TouchAppIcon fontSize="small" /> },
  // Pełny ślad działań (decyzja użytkownika 23.10.2026).
  { key: 'actions', label: 'Działania', icon: <BoltIcon fontSize="small" /> },
  { key: 'clicks', label: 'Kliknięcia', icon: <AdsClickIcon fontSize="small" /> },
  { key: 'timeline', label: 'Oś czasu konta', icon: <TimelineIcon fontSize="small" /> },
  { key: 'errors', label: 'Odmowy i błędy', icon: <ErrorOutlineIcon fontSize="small" /> },
  { key: 'daily', label: 'Aktywność dzienna', icon: <CalendarMonthIcon fontSize="small" /> },
];

const DAY_MS = 86_400_000;

/** Okres domyślny — ostatnie 7 dni polskich z dzisiejszym. */
export function defaultPeriod(now: Date = new Date()): { from: string; to: string } {
  return { from: clientDayKey(new Date(now.getTime() - 6 * DAY_MS)), to: clientDayKey(now) };
}

const exportCsv = (rows: readonly object[], columns: readonly CsvColumn[], name: string) =>
  downloadCSV(toCsv(rows as unknown as RaRecord[], columns), name);

export const UsageAnalytics = () => {
  const on = useFeatureState(FEATURE_USAGE_TELEMETRY);
  const { permissions } = usePermissions<string>();
  if (on === undefined) return <FeaturesLoading />;
  if (!on || permissions !== USAGE_REPORT_ROLE) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('usage-analytics')} />
        <Alert severity="info">{on ? ADMIN_ONLY_NOTE : FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <UsageAnalyticsScreen />;
};

const SelectFilter = ({
  label,
  value,
  onChange,
  options,
  testId,
  emptyLabel = 'wszystkie',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  testId: string;
  /** Podpis pustej wartości — domyślnie „wszystkie”. */
  emptyLabel?: string;
}) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={{ minWidth: 170 }}
    slotProps={{ htmlInput: { 'data-testid': testId } }}
  >
    <MenuItem value="">{emptyLabel}</MenuItem>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

const ExportButton = ({ onClick }: { onClick: () => void }) => (
  <Button size="small" startIcon={<DownloadIcon />} onClick={onClick} data-testid="usage-export-csv">
    Eksport CSV
  </Button>
);

const UsageAnalyticsScreen = () => {
  const [query, setQuery] = useState<UsageQuery>(() => defaultPeriod());
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('paths');
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purged, setPurged] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const impersonationFeatureOn = useFeatureState(FEATURE_IMPERSONATION) === true;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchUsageReport(query)
      .then((r) => {
        if (!alive) return;
        setReport(r);
        setError(null);
      })
      .catch(() => {
        if (alive) setError(LOAD_ERROR);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [query, reload]);

  const set = (patch: Partial<UsageQuery>) => setQuery((q) => ({ ...q, ...patch }));
  const options = report?.options;
  const suffix = `${query.from}_${query.to}`;
  // „Zaloguj jako” (02.11.2026): filtr i liczba sesji przejętych — przy
  // włączonej funkcji albo gdy takie zdarzenia są w okresie.
  const impersonationShown =
    impersonationFeatureOn || (report?.impersonatedEvents ?? 0) > 0 || Boolean(query.impersonation);

  return (
    <Box sx={{ p: 2 }} data-testid="usage-analytics">
      <Title title={menuLabel('usage-analytics')} />
      <Alert severity="info" icon={<PrivacyTipIcon />} sx={{ mb: 2 }} data-testid="usage-intro">
        {INTRO_NOTE}
        <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
          {CONSENT_NOTE}
        </Box>
        {report ? (
          <Box component="span" sx={{ display: 'block', mt: 0.5 }} data-testid="usage-retention">
            {retentionText(report.retentionDays)} To środowisko: {environmentLabel(report.environment)}.
          </Box>
        ) : null}
        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
          {TRACE_NOTE}
        </Box>
        <Stack component="span" direction="row" spacing={1} useFlexGap sx={{ mt: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={exporting}
            onClick={() => {
              setExporting(true);
              setExportError(null);
              downloadUsageExport(query)
                .catch(() => setExportError(EXPORT_ALL_ERROR))
                .finally(() => setExporting(false));
            }}
            data-testid="usage-export-all"
          >
            {EXPORT_ALL_BUTTON}
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setPurgeOpen(true)}
            data-testid="usage-purge-open"
          >
            {PURGE_BUTTON}
          </Button>
        </Stack>
      </Alert>
      {exportError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      ) : null}
      {purged ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPurged(null)} data-testid="usage-purge-done">
          {purged}
        </Alert>
      ) : null}
      {purgeOpen ? (
        <UsagePurgeDialog
          onClose={() => setPurgeOpen(false)}
          onDone={(r) => {
            setPurgeOpen(false);
            setPurged(purgeDoneText(r.removed, r.before));
            setReload((n) => n + 1);
          }}
        />
      ) : null}

      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }} data-testid="usage-filters">
        <TextField
          type="date"
          size="small"
          label="Od"
          value={query.from}
          onChange={(e) => e.target.value && set({ from: e.target.value })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'usage-filter-from' } }}
        />
        <TextField
          type="date"
          size="small"
          label="Do"
          value={query.to}
          onChange={(e) => e.target.value && set({ to: e.target.value })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'usage-filter-to' } }}
        />
        <SelectFilter
          label="Dział"
          value={query.department ?? ''}
          onChange={(v) => set({ department: v || undefined })}
          options={(options?.departments ?? []).map((d) => ({ value: d, label: departmentLabel(d) }))}
          testId="usage-filter-department"
        />
        <SelectFilter
          label="Rola"
          value={query.role ?? ''}
          onChange={(v) => set({ role: v || undefined })}
          options={(options?.roles ?? []).map((r) => ({ value: r, label: roleLabel(r) }))}
          testId="usage-filter-role"
        />
        <SelectFilter
          label="Konto"
          value={query.userId ?? ''}
          onChange={(v) => set({ userId: v || undefined })}
          options={(options?.users ?? []).map((u) => ({ value: String(u.id), label: userLabel(u) }))}
          testId="usage-filter-user"
        />
        <SelectFilter
          label="Środowisko"
          value={query.environment ?? ''}
          onChange={(v) => set({ environment: v || undefined })}
          options={(options?.environments ?? []).map((e) => ({ value: e, label: environmentLabel(e) }))}
          testId="usage-filter-environment"
        />
        {impersonationShown ? (
          <SelectFilter
            label="Sesje przejęte"
            value={query.impersonation ?? ''}
            onChange={(v) => set({ impersonation: v || undefined })}
            options={IMPERSONATION_OPTIONS}
            testId="usage-filter-impersonation"
            emptyLabel="pomijane"
          />
        ) : null}
        {loading ? <CircularProgress size={22} sx={{ alignSelf: 'center' }} /> : null}
      </Stack>
      {impersonationShown ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="usage-impersonation-note">
          {IMPERSONATION_NOTE}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {report ? (
        <>
          <Box data-testid="usage-totals" sx={{ mb: 2 }}>
            <TileGrid>
              <Tile label="Zdarzenia" value={report.totals.events} testId="usage-total-events" />
              <Tile label="Użytkownicy" value={report.totals.users} testId="usage-total-users" />
              <Tile label="Wejścia na ekrany" value={report.totals.screens} />
              <Tile label="Kliknięcia" value={report.totals.clicks} />
              <Tile label="Zatwierdzenia okien" value={report.totals.dialogConfirms} />
              <Tile label="Działania" value={report.totals.actions} testId="usage-total-actions" />
              <Tile label="Okna" value={report.totals.dialogs} />
              <Tile label="Filtry i strony list" value={report.totals.filters} />
              <Tile label="Eksporty" value={report.totals.exports} />
              {impersonationShown ? (
                <Tile
                  label="W czasie przejęcia (poza raportem)"
                  value={report.impersonatedEvents ?? 0}
                  testId="usage-total-impersonated"
                />
              ) : null}
              <Tile
                label="Odmowy i błędy"
                value={report.totals.apiErrors}
                highlight={report.totals.apiErrors > 0 ? 'ostrzezenie' : undefined}
              />
            </TileGrid>
          </Box>

          <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)} variant="scrollable" sx={{ mb: 1 }}>
            {TABS.map((t) => (
              <Tab
                key={t.key}
                value={t.key}
                label={t.label}
                icon={t.icon}
                iconPosition="start"
                data-testid={`usage-tab-${t.key}`}
              />
            ))}
          </Tabs>

          {tab === 'paths' ? (
            <PathsView report={report} onExport={() => exportCsv(report.paths, PATH_CSV, `pomiar-sciezki_${suffix}`)} />
          ) : tab === 'screens' ? (
            <ScreensView
              report={report}
              onExportScreens={() => exportCsv(report.topScreens, SCREEN_CSV, `pomiar-ekrany_${suffix}`)}
              onExportElements={() => exportCsv(report.topElements, ELEMENT_CSV, `pomiar-przyciski_${suffix}`)}
            />
          ) : tab === 'actions' ? (
            <ActionsView actions={report.topActions} routeLabel={routeLabel} suffix={suffix} />
          ) : tab === 'clicks' ? (
            <ClicksView
              clicks={report.topClicks}
              interactions={report.interactions}
              routeLabel={routeLabel}
              suffix={suffix}
            />
          ) : tab === 'timeline' ? (
            <TimelineView query={query} routeLabel={routeLabel} />
          ) : tab === 'errors' ? (
            <ErrorsView report={report} onExport={() => exportCsv(report.apiErrors, ERROR_CSV, `pomiar-bledy_${suffix}`)} />
          ) : (
            <DailyView report={report} onExport={() => exportCsv(report.dailyActivity, DAILY_CSV, `pomiar-aktywnosc_${suffix}`)} />
          )}
        </>
      ) : null}
    </Box>
  );
};

const Header = ({ title, children }: { title: string; children?: ReactNode }) => (
  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 0.5 }}>
    <Typography variant="h6">{title}</Typography>
    {children}
  </Stack>
);

const Empty = ({ text }: { text: string }) => (
  <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
    {text}
  </Typography>
);

const PathsView = ({ report, onExport }: { report: UsageReport; onExport: () => void }) => (
  <Box data-testid="usage-paths">
    <Header title="Ścieżki pracy">
      <ExportButton onClick={onExport} />
    </Header>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {PATHS_NOTE}
    </Typography>
    {report.pathsTruncated ? (
      <Alert severity="warning" sx={{ mb: 1 }}>
        Zdarzeń w tym okresie jest bardzo dużo — ścieżki policzono z pierwszych 200 000. Zawęź okres albo filtry.
      </Alert>
    ) : null}
    <Table size="small" aria-label="Ścieżki pracy">
      <TableHead>
        <TableRow>
          <TableCell>Ścieżka</TableCell>
          <TableCell>Priorytet</TableCell>
          <TableCell align="right">Kroki mierzalne</TableCell>
          <TableCell align="right">Zaczęte (osoby)</TableCell>
          <TableCell align="right">Do końca (osoby)</TableCell>
          <TableCell>Gdzie się zatrzymują</TableCell>
          <TableCell>Mediana czasu</TableCell>
          <TableCell>Działy</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {report.paths.map((p) => (
          <TableRow key={p.id} data-testid={`usage-path-row-${p.id}`}>
            <TableCell>
              {p.id}
              {p.status && p.status !== 'aktywna' ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {p.status}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>{p.priority ?? '—'}</TableCell>
            <TableCell align="right">
              <Tooltip title={`Kroki bez pomiaru: ${unmeasuredText(p)}`}>
                <span>
                  {p.measurableSteps} z {p.steps.length}
                </span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              {p.started} ({p.startedUsers})
            </TableCell>
            <TableCell align="right">
              {p.completed} ({p.completedUsers})
            </TableCell>
            <TableCell>{stopsText(p)}</TableCell>
            <TableCell>{durationText(p.medianDurationSec)}</TableCell>
            <TableCell>
              {p.departments.map((d) => (
                <Chip key={d} size="small" label={departmentLabel(d)} sx={{ mr: 0.5 }} />
              ))}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const ScreensView = ({
  report,
  onExportScreens,
  onExportElements,
}: {
  report: UsageReport;
  onExportScreens: () => void;
  onExportElements: () => void;
}) => (
  <Box data-testid="usage-screens" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
    <Box>
      <Header title="Najczęściej używane ekrany">
        <ExportButton onClick={onExportScreens} />
      </Header>
      {report.topScreens.length === 0 ? (
        <Empty text="Brak wejść na ekrany w tym okresie." />
      ) : (
        <Table size="small" aria-label="Ekrany">
          <TableHead>
            <TableRow>
              <TableCell>Ekran</TableCell>
              <TableCell align="right">Wejścia</TableCell>
              <TableCell align="right">Osoby</TableCell>
              <TableCell align="right">Czas (mediana)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.topScreens.map((s) => (
              <TableRow key={s.route}>
                <TableCell>
                  {routeLabel(s.route)}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {s.route}
                  </Typography>
                </TableCell>
                <TableCell align="right">{s.entries}</TableCell>
                <TableCell align="right">{s.users}</TableCell>
                <TableCell align="right">{durationText(s.medianTimeSec)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
    <Box>
      <Header title="Najczęściej używane przyciski">
        <ExportButton onClick={onExportElements} />
      </Header>
      {report.topElements.length === 0 ? (
        <Empty text="Brak kliknięć w tym okresie." />
      ) : (
        <Table size="small" aria-label="Przyciski">
          <TableHead>
            <TableRow>
              <TableCell>Element</TableCell>
              <TableCell>Najczęściej na ekranie</TableCell>
              <TableCell align="right">Kliknięcia</TableCell>
              <TableCell align="right">Osoby</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.topElements.map((e) => (
              <TableRow key={e.elementId}>
                <TableCell sx={{ wordBreak: 'break-all' }}>
                  {e.elementId}
                  {e.contract ? <Chip size="small" label="z kontraktu" color="info" variant="outlined" sx={{ ml: 0.5 }} /> : null}
                </TableCell>
                <TableCell>{routeLabel(e.route)}</TableCell>
                <TableCell align="right">{e.count}</TableCell>
                <TableCell align="right">{e.users}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  </Box>
);

const ErrorsView = ({ report, onExport }: { report: UsageReport; onExport: () => void }) => (
  <Box data-testid="usage-errors">
    <Header title="Odmowy i błędy według ekranu (odczyty i działania)">
      <ExportButton onClick={onExport} />
    </Header>
    {report.apiErrors.length === 0 ? (
      <Empty text="Brak odmów i błędów w tym okresie." />
    ) : (
      <Table size="small" aria-label="Odmowy i błędy">
        <TableHead>
          <TableRow>
            <TableCell>Ekran</TableCell>
            <TableCell>Kod</TableCell>
            <TableCell>Zapytanie</TableCell>
            <TableCell align="right">Liczba</TableCell>
            <TableCell align="right">Osoby</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.apiErrors.map((e, i) => (
            <TableRow key={`${e.route}-${e.statusCode}-${e.apiEndpoint}-${i}`}>
              <TableCell>{routeLabel(e.route)}</TableCell>
              <TableCell>
                <Chip size="small" label={e.statusCode} color={e.statusCode >= 500 ? 'error' : 'warning'} variant="outlined" />
              </TableCell>
              <TableCell sx={{ wordBreak: 'break-all' }}>{e.apiEndpoint ?? '—'}</TableCell>
              <TableCell align="right">{e.count}</TableCell>
              <TableCell align="right">{e.users}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Box>
);

/** Aktywność dzienna: dni w wierszach, działy w kolumnach — „zdarzenia (osoby)”. */
const DailyView = ({ report, onExport }: { report: UsageReport; onExport: () => void }) => {
  const { days, departments, cell } = useMemo(() => {
    const ds = Array.from(new Set(report.dailyActivity.map((r) => r.day))).sort();
    const deps = Array.from(new Set(report.dailyActivity.map((r) => r.department ?? ''))).sort();
    const map = new Map(report.dailyActivity.map((r) => [`${r.day}|${r.department ?? ''}`, r]));
    return { days: ds, departments: deps, cell: (d: string, dep: string) => map.get(`${d}|${dep}`) };
  }, [report]);
  return (
    <Box data-testid="usage-daily">
      <Header title="Aktywność dzienna według działu">
        <ExportButton onClick={onExport} />
      </Header>
      {days.length === 0 ? (
        <Empty text="Brak aktywności w tym okresie." />
      ) : (
        <Table size="small" aria-label="Aktywność dzienna">
          <TableHead>
            <TableRow>
              <TableCell>Dzień</TableCell>
              {departments.map((d) => (
                <TableCell key={d} align="right">
                  {departmentLabel(d || null)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => (
              <TableRow key={day} data-testid={`usage-day-${day}`}>
                <TableCell>{day}</TableCell>
                {departments.map((d) => {
                  const c = cell(day, d);
                  return (
                    <TableCell key={d} align="right">
                      {c ? `${c.events} (${c.users})` : '—'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" color="text.secondary">
        W komórce: zdarzenia (osoby). Dni według czasu polskiego.
      </Typography>
    </Box>
  );
};
